import { useJobsitesSelection } from 'graphql/client/useJobsitesSelection';
import { openLookerReports } from 'looker';
import { LookerFilter, LookerReport } from 'looker/types';
import React, { ReactElement, useEffect } from 'react';
import moment from 'moment';
import { useLocation } from 'react-router-dom';

import { Filter, Button, TabConfig } from '@odin-labs/components';
import { ApolloError } from '@apollo/client';
import {
  GetJobsiteWorkerDashboardQueryVariables,
  GetJobsiteWorkerReportsTimeBreakdown,
  useGenerateLookerJobsiteWorkerReportMutation,
  useGetJobsiteWorkerDashboardLazyQuery,
} from 'apollo/generated/client-operations';

import { AuthContext } from 'auth';
import { useAvailableJobsites } from 'graphql/client/useAvailableJobsites';
import { ensureNonEmptyItems, getCurrentISOFormattedDate } from 'utils';
import { paginationSizePerPage as limit } from 'utils/constants';
import { getGraphQLError } from 'utils/error';
import { getJobsiteDateTime } from 'utils/dates';
import { isContractorAdminPortal } from 'utils/contractorMemberUser';
import { getJobsiteIdsForDevelopers } from 'utils/filters';
import { usePageQueryParams } from 'utils/usePageQueryParams';
import { AlertService } from 'components/alertService';
import { NewHeader } from 'components/header/NewHeader';
import { LoadingError } from 'components/loadingError';
import { RoutedTabsPages, useRoutedTabs } from 'components/tabs';
import { LockedFeatureAlert } from 'components/lockedFeatureAlert';
import { NewHeaderActionsProps } from 'components/header/types';
import { Container } from 'components/container';
import {
  ArrowToBottomIcon,
  ChartSimpleIcon,
  DataTableIcon,
  MapLocationDotIcon,
  UserHardHatIcon,
} from 'components/icons';
import { ReportDashboardMapsTab } from 'containers/reportDashboard/tabs/maps/ReportDashboardMapsTab';
import { ReportDashboardGraphsTab } from 'containers/reportDashboard/tabs/graphs/ReportDashboardGraphsTab';
import { getFilterItems, getFiltersOptions } from './helpers/tables';
import {
  ReportDashboardStatsTab,
  ReportingDashboardTabProps,
  ReportDashboardWorkersTab,
} from './tabs';

import { DashboardFilters } from './types';

export function ReportDashboardContainer(): ReactElement {
  const location = useLocation();

  const baseRoute = '/reports';

  const defaultStartDate = moment(getCurrentISOFormattedDate());
  const defaultEndDate = moment(getCurrentISOFormattedDate());

  const tabsConfig: TabConfig<ReportingDashboardTabProps>[] =
    ensureNonEmptyItems([
      {
        name: `Stats`,
        relativePath: '',
        component: ReportDashboardStatsTab,
        leftIcon: DataTableIcon,
      },
      {
        name: `Graphs`,
        relativePath: '/graphs',
        component: ReportDashboardGraphsTab,
        leftIcon: ChartSimpleIcon,
      },
      {
        name: `Workers`,
        relativePath: '/workers',
        component: ReportDashboardWorkersTab,
        leftIcon: UserHardHatIcon,
      },
      {
        name: `Maps`,
        relativePath: '/maps',
        component: ReportDashboardMapsTab,
        leftIcon: MapLocationDotIcon,
      },
    ]);

  const {
    page,
    orderBy: sortField,
    orderByDesc: isDescending,
    startDate,
    endDate,
    developerIds,
    jobsiteIds,
    contractor,
    currentlyOnsite,
    compliant,
    overnight,
    trade,
    jobTitle,
    updateUrl,
    loading: isUrlLoading,
  } = usePageQueryParams({
    updateJobsiteSelection: true,
    defaultValues: {
      startDate: defaultStartDate,
      endDate: defaultEndDate,
    },
  });

  const offset = limit * page;

  const isCurrentlyOnsiteVisible =
    defaultStartDate.isSame(startDate, 'day') &&
    defaultEndDate.isSame(endDate, 'day');

  const { currentUser: user } = React.useContext(AuthContext);
  const isFeatureLocked = !user.hasPaidAccess;
  const contractorAdmin = isContractorAdminPortal(user);
  const contractorFilter = contractor?.length ? contractor : null;

  const {
    jobsites,
    loading: userJobsitesLoading,
    error: userJobsitesError,
  } = useAvailableJobsites();

  const startJobsiteDateTime = getJobsiteDateTime({ date: startDate });
  const endJobsiteDateTime = getJobsiteDateTime({ date: endDate });

  const getVariables = (
    overrides?: Partial<GetJobsiteWorkerDashboardQueryVariables>,
  ): GetJobsiteWorkerDashboardQueryVariables => ({
    limit,
    offset,
    sortField,
    sortDir: isDescending ? 'desc' : null,
    startDate: startJobsiteDateTime,
    endDate: endJobsiteDateTime,
    jobsiteIds,
    active: currentlyOnsite ? currentlyOnsite.toLowerCase() === 'yes' : null,
    compliant: compliant ? compliant.toLowerCase() === 'yes' : null,
    overnight: overnight ? overnight.toLowerCase() === 'yes' : null,
    contractor: contractorFilter,
    trade,
    jobTitle,
    ...overrides,
    timeBreakdown: GetJobsiteWorkerReportsTimeBreakdown.Day,
    // fitoutTenant,
    // workerIds,
  });

  const [
    reloadReports,
    {
      data: workerReportsData,
      loading: workerReportsLoading,
      error: workerReportsError,
    },
  ] = useGetJobsiteWorkerDashboardLazyQuery({
    variables: getVariables(),
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (isUrlLoading) return;
    if (location.search || contractorAdmin) {
      reloadReports();
    }
  }, [location.search, isUrlLoading, contractorAdmin]);

  const filtersOptions = React.useMemo(
    () =>
      getFiltersOptions({
        dashboardData: workerReportsData?.getJobsiteWorkerDashboard,
        jobsites,
        overnight,
      }),
    [workerReportsData?.getJobsiteWorkerDashboard, jobsites, overnight],
  );

  const filterItems = React.useMemo(
    () =>
      getFilterItems({
        filtersOptions,
        developerIds,
        jobsiteIds,
        contractor,
        trade,
        jobTitle,
        compliant,
        overnight,
        currentlyOnsite,
        startDate,
        endDate,
        defaultStartDate,
        defaultEndDate,
      }),
    [
      filtersOptions,
      developerIds,
      jobsiteIds,
      contractor,
      trade,
      jobTitle,
      compliant,
      overnight,
      currentlyOnsite,
      startDate,
      endDate,
      defaultStartDate,
      defaultEndDate,
    ],
  );

  const [
    generateLookerJobsiteWorkerReport,
    { loading: generateLookerJobsiteWorkerReportLoading },
  ] = useGenerateLookerJobsiteWorkerReportMutation({
    onError: (apolloError: ApolloError) => {
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(apolloError),
      );
    },
    onCompleted: (response) => {
      window.open(response?.generateLookerJobsiteWorkerReport?.downloadUrl);
    },
  });

  const loading =
    workerReportsLoading ||
    generateLookerJobsiteWorkerReportLoading ||
    userJobsitesLoading;
  const error = workerReportsError || userJobsitesError;

  const { tabs, currentTab } = useRoutedTabs({
    tabsConfig,
    baseRoute,
    search: location.search,
  });

  const tabsPageProps: ReportingDashboardTabProps = {
    data: workerReportsData?.getJobsiteWorkerDashboard,
    loading,
    error,
    jobsites,
    isCurrentlyOnsiteVisible,
  };

  const onFilterChangeHandler = (
    changedFilters: Partial<DashboardFilters>,
  ): void => {
    const {
      dateRange: dateRangeFilter,
      currentlyOnsite: currentlyOnsiteFilter,
      ...restChangedFilters
    } = changedFilters;
    const newCurrentlyOnsite = dateRangeFilter ? null : currentlyOnsiteFilter;
    const dateRange =
      dateRangeFilter === null
        ? { startDate: null, endDate: null }
        : dateRangeFilter;
    updateUrl({
      page: null,
      currentlyOnsite: newCurrentlyOnsite,
      ...dateRange,
      ...restChangedFilters,
      ...getJobsiteIdsForDevelopers(jobsites, changedFilters.developerIds),
    });
  };

  const headerActionsProps = React.useMemo(
    (): NewHeaderActionsProps => ({
      baseRoute,
      tabsProps: { tabs, currentTab },
      onReloadPressed: reloadReports,
    }),
    [baseRoute, tabs, currentTab, reloadReports],
  );

  const { selectedJobsites } = useJobsitesSelection();

  const openLooker = React.useCallback(() => {
    const selectedJobsiteNames =
      selectedJobsites && selectedJobsites.length > 0
        ? selectedJobsites.map(({ name }) => name).join()
        : '';
    openLookerReports(
      LookerReport.Dashboard,
      {
        [LookerFilter.Jobsite]: selectedJobsiteNames,
        ...(contractorAdmin
          ? {
              [LookerFilter.Contractor]: (
                contractorFilter ??
                workerReportsData?.getJobsiteWorkerDashboard?.contractorOptions
              )
                ?.filter(Boolean)
                .join(','),
            }
          : {}),
      },
      '_blank',
    );
  }, [
    contractorAdmin,
    contractorFilter,
    selectedJobsites,
    workerReportsData?.getJobsiteWorkerDashboard?.contractorOptions,
  ]);

  if (error) {
    return <LoadingError error={error} />;
  }

  if (isFeatureLocked) {
    return (
      <div className="odin-p-8">
        <LockedFeatureAlert />
      </div>
    );
  }

  return (
    <Container className="dashboard-container">
      <div className="odin-flex">
        <NewHeader
          className="odin-w-full"
          title="Reports"
          actionsProps={headerActionsProps}
        />
        {!contractorAdmin && (
          <Button onClick={openLooker} className="odin-ml-2.5 odin-min-w-fit">
            All Reports
          </Button>
        )}
      </div>
      <Filter
        items={filterItems}
        loading={loading}
        onChange={onFilterChangeHandler}
        leftContainerStyle="tablet:odin-w-9/12"
      >
        {(!user.isContractor || isContractorAdminPortal(user)) && (
          <Button
            theme="white"
            size={['base', 'md:xs']}
            text="Download"
            hideTextOnMobile
            icon={ArrowToBottomIcon}
            onClick={async (): Promise<void> => {
              await generateLookerJobsiteWorkerReport({
                variables: getVariables({ limit: null, offset: null }),
              });
            }}
            withSpinner={generateLookerJobsiteWorkerReportLoading}
          />
        )}
      </Filter>

      <RoutedTabsPages
        baseRoute={baseRoute}
        tabs={tabs}
        componentProps={tabsPageProps}
      />
    </Container>
  );
}
